import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { Payment, PaymentService } from '../../../../../core/services/payment.service';
import { ModalService } from '../../../../../core/services/modal.service';
import { CheckoutIframeModalComponent } from '../../../../../shared/components/modal/checkout-iframe-modal/checkout-iframe-modal.component';
import { environment } from '../../../../../../environments';
import {
  LucideAngularModule,
  LoaderIcon,
  RefreshCw,
  AlertCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  FileText,
} from 'lucide-angular';
import { Country } from '../../../../../core/interfaces/doctor.interface';

@Component({
  selector: 'app-payments-liste',
  standalone: true,
  imports: [CommonModule, BannerComponent, LucideAngularModule],
  templateUrl: './liste.component.html',
})
export class PaymentsListeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isDppTheme = environment.theme.default === 'dpp';
  links = [
    { label: 'Votre passeport', href: '/passport' },
    { label: 'Paiements', href: undefined },
  ];

  // Pagination
  currentPage = 1;
  itemsPerPage = 12;
  totalItems = 0;
  Math = Math; // Make Math available in template

  // Data
  payments: Payment[] = [];
  loading = false;
  error: string | null = null;
  searchTerm = '';

  readonly LoaderIcon = LoaderIcon;
  readonly RefreshCw = RefreshCw;
  readonly AlertCircleIcon = AlertCircleIcon;
  readonly ChevronRightIcon = ChevronRightIcon;
  readonly ChevronLeftIcon = ChevronLeftIcon;
  readonly FileText = FileText;
  selectedCountry: Country | null = null;

  constructor(
    private router: Router,
    private paymentService: PaymentService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
          // Mettre à jour les liens avec le code du pays
          this.updateLinks();
        } catch {
          this.selectedCountry = null;
        }
      }
    }
    this.loadPayments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateLinks(): void {
    if (this.selectedCountry?.code) {
      this.links[0].href = `/${this.selectedCountry.code}/passport`;
    }
  }

  loadPayments(): void {
    this.error = null;
    this.loading = true;
    this.paymentService
      .getPayments({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          const raw = Array.isArray(response) ? response : response?.data ?? response?.content ?? response?.items ?? [];
          this.payments = (raw as any[]).map((item) => this.mapApiPaymentToPayment(item));
          this.totalItems = response?.total ?? response?.totalElements ?? response?.totalCount ?? this.payments.length;
        },
        error: (err) => {
          this.loading = false;
          this.payments = [];
          this.error = err?.error?.message ?? err?.message ?? 'Impossible de charger les paiements';
        },
      });
  }

  private mapApiPaymentToPayment(item: any): Payment {
    const raw = item.payment ?? item;
    const id = raw.id ?? raw._id ?? item.id ?? item._id ?? '';
    const dateRaw = raw.date ?? raw.createdAt ?? raw.updatedAt ?? item.date ?? item.createdAt ?? item.updatedAt ?? item.paymentDate;
    const dateStr =
      typeof dateRaw === 'string'
        ? dateRaw.match(/^\d{2}\/\d{2}\/\d{4}/)
          ? dateRaw
          : this.formatDateForDisplay(dateRaw)
        : '';
    const status = (raw.status ?? item.status ?? 'pending').toLowerCase();
    const validStatus: Payment['status'] =
      status === 'completed' || status === 'pending' || status === 'failed' || status === 'cancelled' || status === 'processing'
        ? status
        : status === 'paid' || status === 'succeeded'
          ? 'completed'
          : 'pending';
    return {
      id: raw.id ?? raw._id ?? id,
      transactionId: raw.transactionId ?? item.transactionId ?? raw.reference ?? item.reference ?? String(raw.externalPaymentId ?? id),
      amount: Number(raw.amount ?? item.amount) ?? 0,
      currency: raw.currency ?? item.currency ?? 'XOF',
      status: validStatus,
      paymentType: raw.paymentType ?? item.paymentType ?? raw.type ?? item.type ?? 'Rendez-vous',
      date: dateStr,
      appointmentId: item.appointmentId ?? raw.appointmentId ?? item.reservationId ?? item.reservation?._id,
      description: raw.description ?? item.description ?? item.reason ?? null,
      checkoutLink: raw.checkoutLink ?? item.checkoutLink ?? undefined,
      reference: raw.reference ?? item.reference,
      externalPaymentId: raw.externalPaymentId ?? item.externalPaymentId,
    };
  }

  private formatDateForDisplay(dateValue: string): string {
    if (!dateValue) return '';

    // Si la date est déjà au format DD/MM/YYYY, la retourner telle quelle
    if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateValue;
    }

    // Si c'est une date ISO ou autre format, la convertir
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return dateValue; // Retourner la valeur originale si la conversion échoue
      }
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.warn('Erreur lors du formatage de la date:', dateValue, error);
      return dateValue; // Retourner la valeur originale en cas d'erreur
    }
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 1; // Reset to first page when searching
  }

  get filteredPayments(): Payment[] {
    if (!this.searchTerm.trim()) {
      return this.payments;
    }

    const searchLower = this.searchTerm.toLowerCase();
    return this.payments.filter(
      (payment) =>
        payment.transactionId.toLowerCase().includes(searchLower) ||
        payment.paymentType.toLowerCase().includes(searchLower) ||
        payment.status.toLowerCase().includes(searchLower) ||
        (payment.description && payment.description.toLowerCase().includes(searchLower))
    );
  }

  navigateToDetails(payment: Payment): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/payments`,
      payment.id,
    ]);
  }

  canPay(payment: Payment): boolean {
    return (payment.status === 'pending' || payment.status === 'processing');
  }

  openPaymentModal(payment: Payment, event?: Event): void {
    if (event) event.stopPropagation();
    const checkoutLink = payment.checkoutLink;
    if (checkoutLink) {
      this.modalService.open({
        component: CheckoutIframeModalComponent,
        width: '700px',
        closable: true,
        data: { checkoutLink },
      });
      return;
    }
    this.paymentService.getPaymentDetails(payment.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const link = res?.checkoutLink ?? res?.payment?.checkoutLink;
        if (link) {
          this.modalService.open({
            component: CheckoutIframeModalComponent,
            width: '700px',
            closable: true,
            data: { checkoutLink: link },
          });
        } else {
          this.error = 'Lien de paiement indisponible pour ce paiement.';
        }
      },
      error: () => {
        this.error = 'Impossible de charger les détails du paiement.';
      },
    });
  }

  get paginatedPayments(): Payment[] {
    const filtered = this.filteredPayments;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredPayments.length / this.itemsPerPage);
  }

  get pages(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 5); // Limiter à 5 pages max pour l'affichage
    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, startPage + maxPages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  refreshData(): void {
    this.loadPayments();
  }

  getStatusBadgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'paid':
      case 'succeeded':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'pending':
      case 'processing':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      case 'cancelled':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'paid':
      case 'succeeded':
        return 'Complété';
      case 'pending':
        return 'En attente';
      case 'processing':
        return 'En cours';
      case 'failed':
        return 'Échoué';
      case 'cancelled':
        return 'Annulé';
      default:
        return status || '-';
    }
  }

  formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'XOF',
    }).format(amount);
  }
}
